import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { OverlayView } from "@react-google-maps/api";

import { formatDistance } from "../../search/suppliersMappers";
import { StarIcon } from "./SupplierListCard";

const CALLOUT_GAP = 8;
const VIEWPORT_MARGIN = 12;

const PriceMarker = ({ supplier, selected, onSelect, onOpen }) => {
  const label = `${supplier.name}, ${supplier.priceLabel} per kilogram${
    supplier.distanceKm === null ? "" : `, ${formatDistance(supplier.distanceKm)} away`
  }`;

  const bubbleRef = useRef(null);
  const calloutRef = useRef(null);
  const [anchor, setAnchor] = useState(null);
  const [offsetX, setOffsetX] = useState(0);

  useEffect(() => {
    if (!selected) {
      setAnchor(null);
      return undefined;
    }

    let frameId;
    const measure = () => {
      const bubble = bubbleRef.current;
      if (bubble) {
        const rect = bubble.getBoundingClientRect();
        setAnchor((current) =>
          current &&
          current.top === rect.top &&
          current.left === rect.left &&
          current.width === rect.width
            ? current
            : { top: rect.top, left: rect.left, width: rect.width }
        );
      }
      frameId = requestAnimationFrame(measure);
    };

    frameId = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(frameId);
  }, [selected]);

  useLayoutEffect(() => {
    if (!anchor || !calloutRef.current) {
      setOffsetX(0);
      return;
    }

    const calloutWidth = calloutRef.current.offsetWidth;
    const center = anchor.left + anchor.width / 2;
    const min = VIEWPORT_MARGIN + calloutWidth / 2;
    const max = window.innerWidth - VIEWPORT_MARGIN - calloutWidth / 2;
    const clampedCenter = Math.min(Math.max(center, min), max);
    setOffsetX(clampedCenter - center);
  }, [anchor]);

  return (
    <>
      <OverlayView
        position={{ lat: supplier.latitude, lng: supplier.longitude }}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={(width, height) => ({
          x: -(width / 2),
          y: -height,
        })}
      >
        <div className="home-marker">
          <button
            type="button"
            ref={bubbleRef}
            aria-label={label}
            aria-pressed={selected}
            onClick={(event) => {
              event.stopPropagation();
              onSelect(supplier);
            }}
            className={`home-marker-bubble${
              supplier.highlighted ? " home-marker-bubble-best" : ""
            }${supplier.online ? "" : " home-marker-bubble-offline"}`}
          >
            <b>{supplier.priceLabel}</b>
            <s>{supplier.unitLabel}</s>
          </button>

          <span className="home-marker-tail" />
        </div>
      </OverlayView>

      {selected &&
        anchor &&
        createPortal(
          <div
            ref={calloutRef}
            className="home-marker-callout"
            role="dialog"
            style={{
              top: anchor.top - CALLOUT_GAP,
              left: anchor.left + anchor.width / 2 + offsetX,
            }}
          >
            <p className="home-marker-callout-name">{supplier.name}</p>
            <p className="home-marker-callout-meta">
              {supplier.totalRatings > 0 && (
                <span className="home-marker-callout-rating">
                  <StarIcon />
                  {supplier.rating.toFixed(1)}
                </span>
              )}
              {supplier.totalRatings > 0 && supplier.distanceKm !== null && (
                <span className="home-marker-callout-dot" aria-hidden="true" />
              )}
              {supplier.distanceKm !== null && (
                <span>{formatDistance(supplier.distanceKm)}</span>
              )}
            </p>
            <p className="home-marker-callout-price">
              {supplier.priceLabel} <span>pKG</span>
            </p>
            <button
              type="button"
              className="home-marker-callout-action"
              onClick={(event) => {
                event.stopPropagation();
                onOpen(supplier);
              }}
            >
              View supplier
            </button>
          </div>,
          document.body
        )}
    </>
  );
};

const areEqual = (previous, next) =>
  previous.selected === next.selected &&
  previous.supplier.id === next.supplier.id &&
  previous.supplier.latitude === next.supplier.latitude &&
  previous.supplier.longitude === next.supplier.longitude &&
  previous.supplier.priceLabel === next.supplier.priceLabel &&
  previous.supplier.unitLabel === next.supplier.unitLabel &&
  previous.supplier.highlighted === next.supplier.highlighted &&
  previous.supplier.online === next.supplier.online &&
  previous.supplier.name === next.supplier.name &&
  previous.supplier.rating === next.supplier.rating &&
  previous.supplier.totalRatings === next.supplier.totalRatings &&
  previous.supplier.distanceKm === next.supplier.distanceKm &&
  previous.onSelect === next.onSelect &&
  previous.onOpen === next.onOpen;

export default React.memo(PriceMarker, areEqual);
